import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Radar, Trash2, Plus, Eye, EyeOff, Search } from 'lucide-react';
import Modal from './Modal';
import { api, ApiError } from '../../lib/api';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const smallInputClasses =
  'px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-[13px] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 transition-all';

function formatDate(iso) {
  if (!iso) return 'never';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function PagesManager({ open, onClose, account }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [addPath, setAddPath] = useState('');
  const [addTitle, setAddTitle] = useState('');
  const [addingPage, setAddingPage] = useState(false);
  const [addError, setAddError] = useState('');
  const [query, setQuery] = useState('');
  const [generalError, setGeneralError] = useState('');

  const load = useCallback(async () => {
    if (!account?.id) return;
    setLoading(true);
    setGeneralError('');
    try {
      const data = await api.get(`/api/admin/pages/${account.id}`);
      setPages(data.pages || []);
    } catch (err) {
      setGeneralError(err?.message || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  }, [account?.id]);

  useEffect(() => {
    if (open) {
      setScanResult(null);
      setAddPath('');
      setAddTitle('');
      setAddError('');
      setQuery('');
      load();
    }
  }, [open, load]);

  const runScan = async () => {
    if (!account?.id || scanning) return;
    setScanning(true);
    setScanResult(null);
    setGeneralError('');
    try {
      const data = await api.post(`/api/admin/scan-pages/${account.id}`);
      setScanResult(data.result);
      await load();
    } catch (err) {
      setGeneralError(err?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const addPage = async (e) => {
    e.preventDefault();
    if (addingPage) return;
    setAddingPage(true);
    setAddError('');
    try {
      const data = await api.post(`/api/admin/pages/${account.id}`, {
        path: addPath,
        title: addTitle || null,
      });
      setPages((prev) => [...prev, data.page].sort((a, b) => a.path.localeCompare(b.path)));
      setAddPath('');
      setAddTitle('');
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : 'Failed to add page');
    } finally {
      setAddingPage(false);
    }
  };

  const toggleEditable = async (page) => {
    try {
      const data = await api.put(`/api/admin/pages/${account.id}/${page.id}`, {
        isEditable: !page.isEditable,
      });
      setPages((prev) => prev.map((p) => (p.id === page.id ? data.page : p)));
    } catch (err) {
      setGeneralError(err?.message || 'Failed to update page');
    }
  };

  const removePage = async (page) => {
    try {
      await api.delete(`/api/admin/pages/${account.id}/${page.id}`);
      setPages((prev) => prev.filter((p) => p.id !== page.id));
    } catch (err) {
      setGeneralError(err?.message || 'Failed to delete page');
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter((p) =>
      p.path.toLowerCase().includes(q) ||
      (p.title || '').toLowerCase().includes(q)
    );
  }, [pages, query]);

  const editableCount = pages.filter((p) => p.isEditable).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Pages · ${account?.name || ''}`}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-gray-500">
            {pages.length} total · {editableCount} visible to clients
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all"
          >
            Done
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
          <div>
            <div className="text-[13px] text-white font-medium">Sitemap scan</div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Fetches <code className="text-gray-400">{account?.siteUrl || 'siteUrl'}/sitemap.xml</code> and adds new pages.
            </div>
          </div>
          <button
            type="button"
            onClick={runScan}
            disabled={scanning || !account?.siteUrl}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B0FC38]/[0.15] border border-[#B0FC38]/30 text-[13px] text-white font-medium hover:bg-[#B0FC38]/[0.2] transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {scanning ? (
              <>
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                <Radar className="w-3.5 h-3.5" />
                Scan sitemap
              </>
            )}
          </button>
        </div>

        {scanResult && (
          <div className={`px-4 py-2.5 rounded-lg border text-[12px] ${
            scanResult.error
              ? 'bg-yellow-500/[0.08] border-yellow-500/25 text-yellow-300/90'
              : 'bg-[#B0FC38]/[0.06] border-[#B0FC38]/20 text-[#B0FC38]'
          }`}>
            {scanResult.error
              ? `Scan finished with notes: ${scanResult.error.replace(/_/g, ' ')}. Found ${scanResult.total} total, added ${scanResult.added}.`
              : `Scan complete · ${scanResult.total} total pages · ${scanResult.added} new · ${scanResult.unchanged} unchanged${scanResult.source === 'html_links' ? ' · via homepage links (no sitemap found)' : ''}.`}
          </div>
        )}

        {generalError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90">
            {generalError}
          </div>
        )}

        <form onSubmit={addPage} className="flex flex-col gap-2 p-4 rounded-xl bg-white/[0.01] border border-dashed border-white/[0.08]">
          <div className="text-[11px] font-medium text-gray-500 tracking-wider uppercase">Add page manually</div>
          <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr_auto] gap-2">
            <input
              value={addPath}
              onChange={(e) => setAddPath(e.target.value)}
              placeholder="/about"
              className={smallInputClasses}
              required
            />
            <input
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
              placeholder="Title (optional)"
              className={smallInputClasses}
            />
            <button
              type="submit"
              disabled={addingPage || !addPath}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/[0.06] border border-white/[0.1] text-[13px] text-white hover:bg-white/[0.1] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingPage ? (
                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
              Add
            </button>
          </div>
          {addError && <p className="text-[11px] text-red-400">{addError}</p>}
        </form>

        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter pages…"
              className={`${inputClasses} pl-9`}
            />
          </div>
        </div>

        <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] overflow-hidden max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              {pages.length === 0 ? 'No pages yet. Run a scan or add one manually above.' : 'No pages match your filter.'}
            </div>
          ) : (
            <ul>
              {filtered.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.03] last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-gray-200 font-mono truncate" title={p.path}>
                      {p.path}
                    </div>
                    {p.title && <div className="text-[11px] text-gray-500 truncate">{p.title}</div>}
                  </div>
                  <div className="text-[10px] text-gray-600 shrink-0 hidden sm:block">
                    {formatDate(p.lastScanned)}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleEditable(p)}
                    title={p.isEditable ? 'Hide from client' : 'Show to client'}
                    className={`p-1.5 rounded-lg transition-colors ${
                      p.isEditable
                        ? 'text-[#B0FC38] hover:bg-[#B0FC38]/[0.08]'
                        : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.05]'
                    }`}
                  >
                    {p.isEditable ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => removePage(p)}
                    title="Delete page"
                    className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/[0.05] transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
